/**
 * Флаг экрана «Сегодня» — та же механика, что у Rooms Pilot.
 *
 * Статический файл `/ops-today.json` рядом со сборкой: включение
 * поимённо на площадки и учётки, без релиза и без миграций.
 * Выключается подменой файла — это важно для показа фокус-группе:
 * если экран не зайдёт, его убирают одной командой.
 *
 * Копия кода, а не импорт из rooms-pilot, намеренно: два пилота
 * должны включаться и выключаться независимо, и связывать их общим
 * модулем значит однажды выключить оба разом.
 */
export interface OpsTodayFlag {
  enabled: boolean;
  version: string;
  platformIds: number[];
  accountIds: number[];
  /** Новые отели: площадки с id не меньше этого — для всех учёток. */
  fromPlatformId?: number;
}

export const OFF: OpsTodayFlag = {
  enabled: false,
  version: 'off',
  platformIds: [],
  accountIds: [],
};

/** Разбор с проверкой: битый или чужой файл — это «выключено». */
export function parseFlag(value: unknown): OpsTodayFlag {
  const v = value as OpsTodayFlag;
  const ids = (a: unknown): a is number[] =>
    Array.isArray(a) && a.every((x) => Number.isSafeInteger(x) && x > 0);
  return v &&
    typeof v.enabled === 'boolean' &&
    typeof v.version === 'string' &&
    v.version.length > 0 &&
    ids(v.platformIds) &&
    ids(v.accountIds) &&
    (v.fromPlatformId === undefined ||
      (Number.isSafeInteger(v.fromPlatformId) && v.fromPlatformId > 0))
    ? v
    : OFF;
}

/**
 * Пустой `accountIds` означает «все сотрудники разрешённых площадок».
 *
 * Перечислять учётки поимённо нужно для боевого отеля, где пилот
 * показывают двум-трём людям. На стенде это только источник ошибок:
 * список из полусотни id устаревает после каждого пересоздания
 * стенда, и человек, которого забыли, видит «Раздел недоступен» без
 * объяснения причины — что и случилось 27.10.2026.
 *
 * Площадка остаётся обязательной всегда: без неё включение «для
 * всех» означало бы включение на всю систему.
 */
export function eligible(
  flag: OpsTodayFlag,
  account: number | undefined,
  platform: number | undefined,
) {
  if (!flag.enabled || !account || !platform) return false;
  if (flag.fromPlatformId && platform >= flag.fromPlatformId) return true;
  if (!flag.platformIds.includes(platform)) return false;
  return flag.accountIds.length === 0 || flag.accountIds.includes(account);
}
